import { 
  BookOpen, 
  Share2, 
  Feather,
  Map,
  Layers,
  X,
  Plus,
  ChevronRight
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAppStore, type ViewMode } from '@/lib/store';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const navItems: { mode: ViewMode; label: string; icon: typeof BookOpen }[] = [
  { mode: 'manuscript', label: 'Write', icon: Feather },
  { mode: 'constellation', label: 'Bubbles', icon: Share2 },
  { mode: 'timeline', label: 'Timeline', icon: Map },
  { mode: 'compost', label: 'Compost', icon: Layers },
];

export function MobileMenu({ isOpen, onClose }: MobileMenuProps) {
  const { 
    viewMode, 
    setViewMode, 
    activeNovelId,
    novels,
    setActiveNovel,
  } = useAppStore();

  const activeNovel = novels.find(n => n.id === activeNovelId);
  const chapters = activeNovel?.chapters || [];

  const handleNavClick = (mode: ViewMode) => {
    setViewMode(mode);
    onClose();
  };

  const handleChapterClick = (chapterId: string) => {
    const { setActiveChapter } = useAppStore.getState();
    setActiveChapter(chapterId);
    setViewMode('manuscript');
    onClose();
  };

  const handleNovelClick = (novelId: string) => {
    setActiveNovel(novelId);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40 md:hidden"
        onClick={onClose}
      />
      
      {/* Menu Panel */}
      <div 
        className="fixed left-0 top-0 h-full w-[280px] z-50 md:hidden flex flex-col"
        style={{ 
          backgroundColor: 'var(--bg-secondary)',
        }}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between p-4"
          style={{ borderBottom: '1px solid var(--border-light)' }}
        >
          <span 
            className="font-semibold text-lg"
            style={{ color: 'var(--text-primary)' }}
          >
            Menu
          </span>
          <button
            onClick={onClose}
            className="p-2 rounded-lg"
            style={{ 
              color: 'var(--text-muted)',
              backgroundColor: 'var(--bg-tertiary)'
            }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <div className="p-4 space-y-2">
          {navItems.map(({ mode, label, icon: Icon }) => (
            <button
              key={mode}
              onClick={() => handleNavClick(mode)}
              className={cn(
                'w-full flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-all',
                viewMode === mode 
                  ? 'shadow-sm' 
                  : 'active:scale-98'
              )}
              style={{
                backgroundColor: viewMode === mode ? 'var(--bg-card)' : 'transparent',
                color: viewMode === mode ? 'var(--sage)' : 'var(--text-secondary)',
                border: viewMode === mode ? '1px solid var(--border-soft)' : '1px solid transparent'
              }}
            >
              <Icon className="w-5 h-5" />
              <span>{label}</span>
            </button>
          ))}
        </div>

        {/* Divider */}
        <div 
          className="mx-4 h-px"
          style={{ backgroundColor: 'var(--border-light)' }}
        />

        {/* Novels Section */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <span 
              className="text-sm font-medium uppercase tracking-wider"
              style={{ color: 'var(--text-muted)' }}
            >
              Novels
            </span>
            <button
              onClick={() => {
                setViewMode('dashboard');
                onClose();
              }}
              className="text-xs px-3 py-1.5 rounded-full font-medium"
              style={{ 
                color: 'var(--bg-primary)',
                backgroundColor: 'var(--sage)'
              }}
            >
              View All
            </button>
          </div>
          
          <div className="space-y-2">
            {novels.slice(0, 5).map((novel) => (
              <button
                key={novel.id}
                onClick={() => handleNovelClick(novel.id)}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all',
                  activeNovelId === novel.id && 'bg-[var(--sage-light)] bg-opacity-20'
                )}
              >
                <div 
                  className="w-8 h-10 rounded flex-shrink-0"
                  style={{ backgroundColor: novel.coverColor }}
                />
                <span 
                  className="truncate flex-1 text-left"
                  style={{ 
                    color: activeNovelId === novel.id 
                      ? 'var(--text-primary)' 
                      : 'var(--text-secondary)'
                  }}
                >
                  {novel.title}
                </span>
                {activeNovelId === novel.id && (
                  <ChevronRight className="w-4 h-4" style={{ color: 'var(--sage)' }} />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Chapters Section (if novel selected) */}
        {activeNovel && (
          <>
            <div 
              className="mx-4 h-px"
              style={{ backgroundColor: 'var(--border-light)' }}
            />
            
            <div className="p-4 flex-1 overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <span 
                  className="text-sm font-medium uppercase tracking-wider"
                  style={{ color: 'var(--text-muted)' }}
                >
                  Chapters
                </span>
                <button
                  onClick={() => {
                    const { createChapter } = useAppStore.getState();
                    createChapter();
                  }}
                  className="w-7 h-7 rounded-lg flex items-center justify-center"
                  style={{ 
                    color: 'var(--bg-primary)',
                    backgroundColor: 'var(--sage)'
                  }}
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              
              <div className="space-y-1">
                {chapters.map((chapter, index) => (
                  <button
                    key={chapter.id}
                    onClick={() => handleChapterClick(chapter.id)}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all',
                      activeNovel.activeChapterId === chapter.id
                        ? 'bg-[var(--sage-light)] bg-opacity-20'
                        : 'active:bg-[var(--bg-hover)]'
                    )}
                  >
                    <span 
                      className="text-xs w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0"
                      style={{ 
                        backgroundColor: activeNovel.activeChapterId === chapter.id 
                          ? 'var(--sage-soft)' 
                          : 'var(--bg-tertiary)',
                        color: activeNovel.activeChapterId === chapter.id 
                          ? 'white' 
                          : 'var(--text-muted)'
                      }}
                    >
                      {index + 1}
                    </span>
                    <span 
                      className="truncate flex-1 text-left"
                      style={{ 
                        color: activeNovel.activeChapterId === chapter.id 
                          ? 'var(--text-primary)' 
                          : 'var(--text-secondary)'
                      }}
                    >
                      {chapter.title}
                    </span>
                  </button>
                ))}
                
                {chapters.length === 0 && (
                  <div 
                    className="px-3 py-4 text-center text-sm"
                    style={{ color: 'var(--text-muted)' }}
                  >
                    No chapters yet
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Word Count Footer */}
        {activeNovel && (
          <div 
            className="p-4"
            style={{ borderTop: '1px solid var(--border-light)' }}
          >
            <div 
              className="px-4 py-3 rounded-xl"
              style={{ backgroundColor: 'var(--bg-tertiary)' }}
            >
              <div className="flex items-center justify-between text-sm mb-2">
                <span style={{ color: 'var(--text-muted)' }}>Total Words</span>
                <span 
                  className="font-semibold"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {activeNovel.chapters.reduce((sum, ch) => sum + (ch.wordCount || 0), 0).toLocaleString()}
                </span>
              </div>
              <div 
                className="h-2 rounded-full overflow-hidden"
                style={{ backgroundColor: 'var(--border-light)' }}
              >
                <div 
                  className="h-full rounded-full transition-all duration-500"
                  style={{ 
                    backgroundColor: 'var(--sage-soft)',
                    width: `${Math.min(
                      (activeNovel.chapters.reduce((sum, ch) => sum + (ch.wordCount || 0), 0) / activeNovel.targetWordCount) * 100,
                      100
                    )}%`
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
